import React from "react";
import dayjs from "dayjs";

// Tone drives the header strip only — the body stays white so the day number
// keeps maximum contrast.
const TONES = {
  brand: "bg-brand-500",
  warning: "bg-warning-500",
  grey: "bg-grey-500",
  error: "bg-error-500",
  success: "bg-green-500",
};

const SIZES = {
  sm: { box: "w-12", month: "text-[9px] py-0.5", day: "text-base", time: "text-[9px]" },
  md: { box: "w-14", month: "text-[10px] py-0.5", day: "text-xl", time: "text-[10px]" },
  lg: { box: "w-16", month: "text-xs py-1", day: "text-2xl", time: "text-[11px]" },
};

/**
 * A tear-off calendar chip — coloured month strip over a large day number, with
 * an optional time underneath. Reads as a date at a glance, which a plain
 * formatted string does not.
 *
 * The date is rendered as given: convert to whatever zone you want to show
 * BEFORE passing it in.
 *
 * @param {string|Date|dayjs.Dayjs} date  The moment to display.
 * @param {boolean} showTime  Render the time under the day (default true).
 * @param {"brand"|"warning"|"grey"|"error"|"success"} tone  Header strip colour.
 * @param {"sm"|"md"|"lg"} size  Chip size (default "md").
 */
const CalendarDateChip = ({
  date,
  showTime = true,
  tone = "brand",
  size = "md",
  className = "",
}) => {
  const value = date ? dayjs(date) : null;
  const isValid = !!value?.isValid();
  const sizing = SIZES[size] || SIZES.md;

  return (
    <div
      // `self-start` so a flex row with `items-stretch` doesn't grow the chip to
      // the row's height and leave dead space under the time.
      className={`shrink-0 self-start ${sizing.box} rounded-md overflow-hidden border border-solid border-grey-200 bg-white text-center ${className}`}
    >
      <div
        className={`${TONES[tone] || TONES.brand} ${
          sizing.month
        } font-semibold uppercase tracking-wide text-white leading-none`}
      >
        {isValid ? value.format("MMM") : "--"}
      </div>
      <div className="px-1 py-1">
        <div className={`${sizing.day} font-semibold leading-none text-grey-800`}>
          {isValid ? value.format("DD") : "--"}
        </div>
        {showTime && isValid && (
          <div className={`${sizing.time} text-grey-500 mt-1 leading-none`}>
            {value.format("hh:mm A")}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarDateChip;
